/*
 * The onboarding steps, as data. How a step looks — its icon — is the UI's
 * business: see ui/stepIcons.ts.
 */

/** The screen where the therapist picks their tools. */
export type PickerStep = { kind: "picker"; key: string };

/**
 * A step that sends the therapist somewhere to finish the job, rather than
 * doing it here. Used where the answer needs more than a yes: the public
 * profile cannot be published from a modal, because publishing needs a
 * headline and a description the therapist has not written yet — and a
 * directory of blank profiles is worse than a small one.
 */
export type NavigateStep = {
    kind: "navigate";
    key: string;
    path: string;
};

export type Step = PickerStep | NavigateStep;

/**
 * The one screen that sets up a practice.
 *
 * It used to be a question per tool. That reads well at three tools and badly
 * at seven: a therapist answering "ahora no" six times has been asked to
 * decline the product six times before seeing it, and every tool we add is
 * another screen between them and the app. Here the catalogue is the screen —
 * grouped the way the dock groups it, with the everyday ones already ticked —
 * and a new tool is a catalogue row rather than another question.
 */
export const PICKER_STEP: PickerStep = { kind: "picker", key: "picker" };

/**
 * Ticked to begin with: what nearly every online practice runs on.
 *
 * Chosen rather than everything, because a default is a recommendation and
 * recommending all of it recommends nothing. The clinical instruments depend
 * on how somebody works, and Augmented Memory reads session content — that is
 * a yes a therapist should give rather than untick. Charging is left unticked
 * for the same reason: a therapist who bills outside Alchely should not have
 * to notice a payments tool they never asked for.
 *
 * **Nothing that costs money belongs here.** AlchelyMeeting was on this list
 * from before it had a price, which meant somebody who paid for the plan
 * alone was handed a 5,00 €/month module by a screen that never mentioned
 * money and a checkbox they had to notice in order to clear. A default is a
 * recommendation; it cannot also be a charge.
 */
export const DEFAULT_SLUGS: readonly string[] = ["scheduler"];

/**
 * Asked after the tools, once the therapist has seen what Alchely does for
 * their practice: whether they also want to be found by people looking for a
 * therapist. Off unless they say yes — publication is opt-in everywhere else
 * too, and this is where most therapists will first hear about it.
 */
export const PUBLIC_PROFILE_STEP: NavigateStep = {
    kind: "navigate",
    key: "publicProfile",
    path: "/profile?tab=public",
};

export const CATALOG_PATH = "/tools-catalog";
export const PUBLIC_PROFILE_PATH = PUBLIC_PROFILE_STEP.path;

/** Adopting a tool that is already adopted is the outcome we wanted, not a failure. */
export const isAlreadyAdopted = (error: unknown): boolean =>
    (error as { response?: { status?: number } })?.response?.status === 409;
